/**
 * @fileoverview World Element V3 discriminated union and shared enums.
 *
 * This file provides:
 * - The master enum for all world element types
 * - The discriminated union schema (Object, Place, Event, and Organization implemented)
 * - Unified context schema for agents
 *
 * As new element types are implemented (Set, Species, System),
 * they will be added to the discriminated union here.
 */

import { z } from 'zod';
import {
  WorldObjectV3Schema,
  WorldObjectContextV3Schema,
  WorldObjectSnapshotV3Schema,
  WorldObjectIdeaV3Schema,
  type WorldObjectV3,
  type WorldObjectContextV3,
  type WorldObjectSnapshotV3,
  type WorldObjectIdeaV3,
} from './world-object';
import {
  WorldPlaceV3Schema,
  WorldPlaceContextV3Schema,
  WorldPlaceSnapshotV3Schema,
  WorldPlaceIdeaV3Schema,
  type WorldPlaceV3,
  type WorldPlaceContextV3,
  type WorldPlaceSnapshotV3,
  type WorldPlaceIdeaV3,
} from './world-place';
import {
  WorldEventV3Schema,
  WorldEventContextV3Schema,
  WorldEventSnapshotV3Schema,
  WorldEventIdeaV3Schema,
  type WorldEventV3,
  type WorldEventContextV3,
  type WorldEventSnapshotV3,
  type WorldEventIdeaV3,
} from './world-event';
import {
  WorldOrganizationV3Schema,
  WorldOrganizationContextV3Schema,
  WorldOrganizationSnapshotV3Schema,
  WorldOrganizationIdeaV3Schema,
  type WorldOrganizationV3,
  type WorldOrganizationContextV3,
  type WorldOrganizationSnapshotV3,
  type WorldOrganizationIdeaV3,
} from './world-organization';
import {
  WorldSpeciesV3Schema,
  WorldSpeciesContextV3Schema,
  WorldSpeciesSnapshotV3Schema,
  WorldSpeciesIdeaV3Schema,
  type WorldSpeciesV3,
  type WorldSpeciesContextV3,
  type WorldSpeciesSnapshotV3,
  type WorldSpeciesIdeaV3,
} from './world-species';
import {
  WorldSystemV3Schema,
  WorldSystemContextV3Schema,
  WorldSystemSnapshotV3Schema,
  WorldSystemIdeaV3Schema,
  type WorldSystemV3,
  type WorldSystemContextV3,
  type WorldSystemSnapshotV3,
  type WorldSystemIdeaV3,
} from './world-system';
import {
  WorldSetV3Schema,
  WorldSetContextV3Schema,
  WorldSetSnapshotV3Schema,
  WorldSetIdeaV3Schema,
  type WorldSetV3,
  type WorldSetContextV3,
  type WorldSetSnapshotV3,
  type WorldSetIdeaV3,
} from './world-set';

// =============================================================================
// WORLD ELEMENT TYPE ENUM
// =============================================================================

/**
 * All world element types in V3.
 * Each type has distinct fields and behaviors while sharing common patterns.
 */
export const WorldElementTypeV3Enum = z.enum([
  'place',        // Locations, buildings, regions
  'set',          // Staged environments with sensory detail (e.g., a dim-lit war room, a moonlit courtyard)
  'organization', // Groups of people with shared purpose
  'object',       // Artifacts and items (PoC implementation)
  'species',      // Races, creatures, beings
  'system',       // Magic systems, technologies, social structures
  'event',        // Historical events, prophecies, recurring phenomena
]);

export type WorldElementTypeV3 = z.infer<typeof WorldElementTypeV3Enum>;

/**
 * Array of all element types for iteration.
 */
export const WORLD_ELEMENT_TYPES_V3: WorldElementTypeV3[] = WorldElementTypeV3Enum.options;

// =============================================================================
// DISCRIMINATED UNION (Object, Place, Event, Organization implemented)
// =============================================================================

/**
 * The unified world element schema as a discriminated union.
 *
 * Currently includes Object, Place, Event, Organization, and Species.
 * As more types are implemented, they will be added to this union:
 *
 * ```typescript
 * export const WorldElementV3Schema = z.discriminatedUnion('type', [
 *   WorldPlaceV3Schema,
 *   WorldSetV3Schema,
 *   WorldOrganizationV3Schema,
 *   WorldObjectV3Schema,
 *   WorldSpeciesV3Schema,
 *   WorldSystemV3Schema,
 *   WorldEventV3Schema,
 * ]);
 * ```
 */
export const WorldElementV3Schema = z.discriminatedUnion('type', [
  WorldObjectV3Schema,
  WorldPlaceV3Schema,
  WorldSetV3Schema,
  WorldEventV3Schema,
  WorldOrganizationV3Schema,
  WorldSpeciesV3Schema,
  WorldSystemV3Schema,
]);

/**
 * The unified context schema for AI agents.
 * Includes the entity plus resolved latest snapshot.
 */
export const WorldElementContextV3Schema = z.discriminatedUnion('type', [
  WorldObjectContextV3Schema,
  WorldPlaceContextV3Schema,
  WorldSetContextV3Schema,
  WorldEventContextV3Schema,
  WorldOrganizationContextV3Schema,
  WorldSpeciesContextV3Schema,
  WorldSystemContextV3Schema,
]);

/**
 * The unified snapshot schema.
 */
export const WorldElementSnapshotV3Schema = z.union([
  WorldObjectSnapshotV3Schema,
  WorldPlaceSnapshotV3Schema,
  WorldSetSnapshotV3Schema,
  WorldEventSnapshotV3Schema,
  WorldOrganizationSnapshotV3Schema,
  WorldSpeciesSnapshotV3Schema,
  WorldSystemSnapshotV3Schema,
]);

/**
 * The unified idea schema for ideation flows.
 */
export const WorldElementIdeaV3Schema = z.union([
  WorldObjectIdeaV3Schema,
  WorldPlaceIdeaV3Schema,
  WorldSetIdeaV3Schema,
  WorldEventIdeaV3Schema,
  WorldOrganizationIdeaV3Schema,
  WorldSpeciesIdeaV3Schema,
  WorldSystemIdeaV3Schema,
]);

// =============================================================================
// TYPE INFERENCE
// =============================================================================

// Union types for all implemented element types.
export type WorldElementV3 = WorldObjectV3 | WorldPlaceV3 | WorldSetV3 | WorldEventV3 | WorldOrganizationV3 | WorldSpeciesV3 | WorldSystemV3;
export type WorldElementContextV3 = WorldObjectContextV3 | WorldPlaceContextV3 | WorldSetContextV3 | WorldEventContextV3 | WorldOrganizationContextV3 | WorldSpeciesContextV3 | WorldSystemContextV3;
export type WorldElementSnapshotV3 = WorldObjectSnapshotV3 | WorldPlaceSnapshotV3 | WorldSetSnapshotV3 | WorldEventSnapshotV3 | WorldOrganizationSnapshotV3 | WorldSpeciesSnapshotV3 | WorldSystemSnapshotV3;
export type WorldElementIdeaV3 = WorldObjectIdeaV3 | WorldPlaceIdeaV3 | WorldSetIdeaV3 | WorldEventIdeaV3 | WorldOrganizationIdeaV3 | WorldSpeciesIdeaV3 | WorldSystemIdeaV3;

// =============================================================================
// HELPER TYPES
// =============================================================================

/**
 * Type guard to check if an element is of a specific type.
 */
export function isWorldElementType<T extends WorldElementTypeV3>(
  element: WorldElementV3,
  type: T
): element is WorldElementV3 & { type: T } {
  return element.type === type;
}

/**
 * Type for the V3 world elements storage structure in a project.
 */
export const WorldElementsV3StorageSchema = z.object({
  objects: z.array(WorldObjectV3Schema),
  places: z.array(WorldPlaceV3Schema),
  events: z.array(WorldEventV3Schema),
  organizations: z.array(WorldOrganizationV3Schema),
  species: z.array(WorldSpeciesV3Schema),
  systems: z.array(WorldSystemV3Schema),
  sets: z.array(WorldSetV3Schema),
});

export type WorldElementsV3Storage = z.infer<typeof WorldElementsV3StorageSchema>;

/**
 * Type for the ideated world elements storage structure.
 */
export const IdeatedWorldElementsV3StorageSchema = z.object({
  objects: z.array(WorldObjectIdeaV3Schema),
  places: z.array(WorldPlaceIdeaV3Schema),
  sets: z.array(WorldSetIdeaV3Schema),
  events: z.array(WorldEventIdeaV3Schema),
  organizations: z.array(WorldOrganizationIdeaV3Schema),
  species: z.array(WorldSpeciesIdeaV3Schema),
  systems: z.array(WorldSystemIdeaV3Schema),
});

export type IdeatedWorldElementsV3Storage = z.infer<typeof IdeatedWorldElementsV3StorageSchema>;
